
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/ui/page-header';
import { resetUserProgress } from '@/api/module';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const ProfileView = () => {
  const { state, dispatch } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isResetting, setIsResetting] = useState(false);

  const handleEdit = () => {
    navigate('/profile/edit');
  };

  const handleExit = () => {
    navigate('/updates');
  };

  const handleRestartTest = async () => {
    setIsResetting(true);
    try {
      // Call the backend reset endpoint
      await resetUserProgress();
      
      // Clear session storage for SHAPE questions
      sessionStorage.removeItem('shape_question_order');
      
      // Clear all cached data
      await queryClient.invalidateQueries();
      
      // Navigate to the first module
      navigate('/module');
    } catch (error) {
      console.error('Error resetting progress:', error);
      alert('Error resetting progress. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20 safe-area-inset">
      <PageHeader 
        title="Profile"
        variant="title-with-exit"
        onExit={handleExit}
      />

      {/* Content */}
      <div className="flex-1 flex flex-col justify-start px-6 pt-8 sm:pt-16 md:pt-24">
        <div className="mb-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-black mb-6 leading-tight">
            Your profile
          </h2>
          <p className="text-lg sm:text-xl text-[#666666] mb-8 leading-relaxed">
            Review and update your personal information.
          </p>
        </div>

        <div className="bg-[#f7f7f7] rounded-lg p-6 mb-6">
          <div className="space-y-6">
            <div>
              <label className="text-base sm:text-lg font-medium text-[#666666]">Name</label>
              <p className="text-lg sm:text-xl text-black font-medium">
                {user?.firstName} {user?.lastName}
              </p>
            </div>

            <div>
              <label className="text-base sm:text-lg font-medium text-[#666666]">Nickname</label>
              <p className="text-lg sm:text-xl text-black font-medium">
                {user?.nickname || 'Not set'}
              </p>
            </div>

            <div>
              <label className="text-base sm:text-lg font-medium text-[#666666]">Title</label>
              <p className="text-lg sm:text-xl text-black font-medium">
                {user?.title || 'Not set'}
              </p>
            </div>

            <div>
              <label className="text-base sm:text-lg font-medium text-[#666666]">Department</label>
              <p className="text-lg sm:text-xl text-black font-medium">
                {user?.department || 'Not set'}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-5">
          <Button
            onClick={handleEdit}
            className="w-full bg-black text-white hover:bg-[#666666] h-12 sm:h-14 rounded-lg text-base sm:text-lg font-medium min-touch-target touch-manipulation"
          >
            Edit Profile
          </Button>

          {/* Restart Test Button */}
          <div className="flex justify-center">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="text-[#666666] hover:text-black text-sm sm:text-base min-touch-target touch-manipulation"
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Resetting...
                    </span>
                  ) : (
                    'Restart test'
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Restart Test?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to restart the test? All your assessments and progress will be cleared, allowing you to start fresh.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRestartTest} disabled={isResetting}>
                    Yes, Restart Test
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
